import Button from 'ext-ui/components/Button/Button'
import TextAreaInput from 'ext-ui/components/TextAreaInput/TextAreaInput'
import React, { useState } from 'react'
import { ChatMarkdown } from 'src/agent/components/AgentChat/AgentChat.style'
import { useTranslate } from 'src/localization/Trns'
import {
  StreamInterruptAction,
  StreamInterruptDecision,
  StreamInterruptItem,
} from 'src/queries/agent/network/agent.models'

import styles from './InterruptCard.style'

interface InterruptCardProps {
  interrupt: StreamInterruptItem
  decision: StreamInterruptDecision | null
  onDecisionChange: (decision: StreamInterruptDecision) => void
  disabled?: boolean
}

/**
 * Renders a single interrupt item with Approve/Reject actions
 * Reject action supports optional feedback
 */
const InterruptCard: React.FC<InterruptCardProps> = ({
  interrupt,
  decision,
  onDecisionChange,
  disabled = false,
}) => {
  const translate = useTranslate()
  const [showFeedback, setShowFeedback] = useState(false)
  const [feedbackText, setFeedbackText] = useState('')

  const { confirmation } = interrupt

  const handleActionClick = (action: StreamInterruptAction): void => {
    if (action === 'reject') {
      setShowFeedback(true)
    } else {
      setShowFeedback(false)
      onDecisionChange({ type: 'approve' })
    }
  }

  const handleRejectConfirm = (): void => {
    onDecisionChange({ type: 'reject', feedback: feedbackText })
    setShowFeedback(false)
  }

  const handleRejectCancel = (): void => {
    setShowFeedback(false)
    setFeedbackText('')
  }

  return (
    <div css={styles} className="InterruptCard">
      <div className="interrupt-header">
        <span className="tool-name">{confirmation.toolName}</span>
      </div>

      <ChatMarkdown className="interrupt-message">
        {confirmation.message}
      </ChatMarkdown>

      {confirmation.toolInputs &&
        Object.keys(confirmation.toolInputs).length > 0 && (
          <div className="tool-inputs">
            <div className="inputs-label">{translate('Parameters:')}</div>
            <div className="inputs-content">
              {JSON.stringify(confirmation.toolInputs, null, 2)}
            </div>
          </div>
        )}

      {!showFeedback && (
        <div className="actions-wrapper">
          <Button
            variant="primary"
            className={`action-button approve ${decision?.type === 'approve' ? 'selected' : ''}`}
            onClick={() => handleActionClick('approve')}
            disabled={disabled}
          >
            {translate('Approve')}
          </Button>
          <Button
            variant="severe"
            className={`action-button reject ${decision?.type === 'reject' ? 'selected' : ''}`}
            onClick={() => handleActionClick('reject')}
            disabled={disabled}
          >
            {decision?.type === 'reject'
              ? translate('Rejected')
              : translate('Reject')}
          </Button>
        </div>
      )}

      {showFeedback && (
        <div className="feedback-form">
          <TextAreaInput
            label={translate('Reason for rejection (optional):')}
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            disabled={disabled}
            placeholder={translate(
              'What would you like the agent to do instead?',
            )}
            rows={3}
          />
          <div className="feedback-actions">
            <Button
              variant="secondary"
              onClick={handleRejectCancel}
              disabled={disabled}
            >
              {translate('Cancel')}
            </Button>
            <Button
              variant="severe"
              onClick={handleRejectConfirm}
              disabled={disabled}
            >
              {translate('Confirm Rejection')}
            </Button>
          </div>
        </div>
      )}

      {decision?.type === 'reject' && !showFeedback && (
        <div className="feedback-display">
          <strong>{translate('Feedback:')}</strong>{' '}
          {decision.feedback || translate('None')}
          <Button
            variant="text"
            onClick={() => {
              setFeedbackText(decision.feedback || '')
              setShowFeedback(true)
            }}
            disabled={disabled}
            className="edit-button"
          >
            {translate('Edit')}
          </Button>
        </div>
      )}
    </div>
  )
}

export default InterruptCard
